import * as React from 'react';
import {useState, useEffect} from 'react';
import classNames from 'classnames';
import DropDown, {DropDownColor, DropDownOption} from '../../../shared/DropDown';
import {Filter, FilterType, FilterOption} from '../../../shared/CommonInterfaces';
import {FilterSearchTypes, InboundFilterKeys} from '../InboundInterfaces';

interface Props {
  // A record of currently active filters, e.g., { sku: 'ABC-123' }
  activeFilters: Record<string, string>;
  // Callback to inform the parent of a change. An empty value signifies removal.
  onFilterChange: (key: string, value: string) => void;
  // Prop to disable UI elements during data fetching.
  loading?: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-var-requires
const circlePlusIcon = require('../../../../../assets/images/common/icon_circle_plus.svg');

// This is the main dropdown to select a filter type.
const parentFilter: Filter = {
  displayName: 'Select Filter',
  key: 'parent_filter',
  type: FilterType.Dropdown,
  options: [] // Will be populated dynamically
};

// Define each specific filter using the shared `Filter` interface.
const purchaseOrderFilter: Filter = {
  displayName: 'Purchase Order',
  key: InboundFilterKeys.PurchaseOrder,
  type: FilterType.String
};

const inboundIdFilter: Filter = {
  displayName: 'Inbound ID',
  key: InboundFilterKeys.InboundId,
  type: FilterType.String
};

const skuFilter: Filter = {
  displayName: 'SKU',
  key: InboundFilterKeys.Sku,
  type: FilterType.String
};

const trailerNumberFilter: Filter = {
  displayName: 'Trailer Number',
  key: InboundFilterKeys.TrailerNumber,
  type: FilterType.String
};

const vendorFilter: Filter = {
  displayName: 'Vendor Id',
  key: InboundFilterKeys.Vendor,
  type: FilterType.String
};

const moveDateRangeFilter: Filter = {
  displayName: 'Estimated Arrival Date',
  key: InboundFilterKeys.MoveDateRange,
  type: FilterType.Range
};

const moveDateStartFilter: Filter = {
  displayName: 'Arrival Date Start',
  key: InboundFilterKeys.MoveDateStart,
  type: FilterType.Date
};

const moveDateEndFilter: Filter = {
  displayName: 'Arrival Date End',
  key: InboundFilterKeys.MoveDateEnd,
  type: FilterType.Date
};

const filterMap = new Map<string, Filter>([
  [InboundFilterKeys.InboundId, inboundIdFilter],
  [InboundFilterKeys.PurchaseOrder, purchaseOrderFilter],
  [InboundFilterKeys.TrailerNumber, trailerNumberFilter],
  [InboundFilterKeys.Vendor, vendorFilter],
  [InboundFilterKeys.Sku, skuFilter],
  [InboundFilterKeys.MoveDateStart, moveDateStartFilter],
  [InboundFilterKeys.MoveDateEnd, moveDateEndFilter]
]);

// List to display as a filter in UI
const addableFilters: Filter[] = [
  purchaseOrderFilter,
  inboundIdFilter,
  skuFilter,
  trailerNumberFilter,
  vendorFilter,
  moveDateRangeFilter
];

const InboundFilter: React.FC<Props> = ({activeFilters, onFilterChange, loading}) => {
  const [selectedFilter, setSelectedFilter] = useState(parentFilter);
  // The value being typed into the filter input.
  const [filterValue, setFilterValue] = useState('');
  // A trigger to apply the filter.
  const [applyFilter, setApplyFilter] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const addableFilterMap = new Map<string, Filter>(addableFilters.map((f) => [f.key, f]));

  // Ignore active filter from the filter menu
  const availableFilters = addableFilters.filter((f) => {
    // Move date range filter is available only if neither start nor end date filters are active
    if (f.key === moveDateRangeFilter.key) {
      return !activeFilters[InboundFilterKeys.MoveDateStart] && !activeFilters[InboundFilterKeys.MoveDateEnd];
    }

    return !activeFilters[f.key];
  });

  const filters: Filter[] = [parentFilter, ...availableFilters];

  // Populate the parent filter's options with available filters.
  parentFilter.options = filters.map((filter) => ({
    displayName: filter.displayName,
    value: filter.key
  }));

  useEffect(() => {
    if (applyFilter) {
      // If the parent filter is selected, it means we've just chosen a filter type.
      // We update the selectedFilter state to show the correct input for that type.
      if (selectedFilter?.key === parentFilter.key) {
        const childFilter = filterMap.get(filterValue);
        if (childFilter) {
          setSelectedFilter(childFilter);
          setFilterValue(''); // Clear value for the new input
        }
      } else if (selectedFilter && selectedFilter.key === moveDateRangeFilter.key) {
        if (startDate.trim()) {
          onFilterChange(InboundFilterKeys.MoveDateStart, startDate.trim());
        }
        if (endDate.trim()) {
          onFilterChange(InboundFilterKeys.MoveDateEnd, endDate.trim());
        }
        setSelectedFilter(parentFilter);
        setFilterValue('');
        setStartDate('');
        setEndDate('');
      } else if (selectedFilter && filterValue.trim()) {
        // If a specific filter is selected, apply it by calling the parent's handler.
        onFilterChange(selectedFilter.key, filterValue.trim());
        // Reset the UI to show the '+' button again.
        setSelectedFilter(parentFilter);
        setFilterValue('');
      }
      setApplyFilter(false);
    }
  }, [applyFilter, filterValue, onFilterChange, selectedFilter, filterMap]);

  const handleCancel = () => {
    setSelectedFilter(parentFilter);
    setFilterValue('');
    setStartDate('');
    setEndDate('');
  };

  const handleRemoveFilter = (filterKey: string) => {
    setSelectedFilter(parentFilter);

    onFilterChange(filterKey, '');
  };

  const handleSelectFilterType = (option: DropDownOption) => {
    const filterKey = String(option.value);
    const newSelectedFilter = addableFilterMap.get(filterKey);
    if (newSelectedFilter && newSelectedFilter.key !== parentFilter.key) {
      setSelectedFilter(newSelectedFilter);
    }
  };

  const handleInputKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      setApplyFilter(true);
    }
  };

  const checkSelectedFilter = (filter: Filter) => {
    switch (filter.key) {
      case trailerNumberFilter.key:
      case vendorFilter.key:
      case purchaseOrderFilter.key: {
        return FilterSearchTypes.Contains;
      }
      case moveDateStartFilter.key:
      case moveDateEndFilter.key:
      case inboundIdFilter.key:
      case skuFilter.key:
      default: {
        return FilterSearchTypes.Is;
      }
    }
  };

  const activeFilterPills = Object.entries(activeFilters).map(([key, value]) => {
    const filter = filterMap.get(key);

    if (!filter) return null;

    return (
      <li key={key}>
        <div className="active-filter">
          <span className="active-filter-label">
            {filter.displayName} <strong>"{value}"</strong>
          </span>
          <span className="active-filter-action" onClick={() => handleRemoveFilter(key)}>
            &times;
          </span>
        </div>
      </li>
    );
  });

  // The UI for adding a new filter, driven by `selectedFilter`.
  const filterInput = () => {
    if (!selectedFilter) return null;

    switch (selectedFilter.type) {
      case FilterType.Dropdown: {
        // This is for the parentFilter
        const options: DropDownOption[] = selectedFilter.options.map((option: FilterOption) => ({
          name: option.displayName,
          value: String(option.value)
        }));
        return (
          <>
            <li>Filter by:</li>
            <li id="filter-dropdown" data-testid="select-filter-dropdown">
              <DropDown
                options={options}
                onSelect={handleSelectFilterType}
                prefixText={selectedFilter.placeHolder}
                selected={selectedFilter.placeHolder ? null : options[0]}
                color={DropDownColor.white}
              />
            </li>

            {selectedFilter.key !== parentFilter.key && (
              <li>
                <a className="text-danger" onClick={handleCancel}>
                  Cancel
                </a>
              </li>
            )}
          </>
        );
      }
      case FilterType.String: {
        return (
          <>
            <li>Filter by {selectedFilter.displayName}:</li>
            <li className="input-with-label">
              <label className="custom-label">{checkSelectedFilter(selectedFilter)}</label>
              <input
                className="custom-input"
                type="text"
                value={filterValue}
                onChange={(e) => setFilterValue(e.target.value)}
                onKeyUp={handleInputKeyUp}
                placeholder="Enter value..."
                autoFocus
              />
            </li>
            <li>
              <a
                id="add-filter"
                onClick={() => setApplyFilter(true)}
                className={classNames({disabled: !filterValue.trim()})}
              >
                Add
              </a>
            </li>
            <li>
              <a className="text-danger" onClick={handleCancel}>
                Cancel
              </a>
            </li>
          </>
        );
      }
      case FilterType.Range: {
        return (
          <>
            <li>{selectedFilter.displayName}:</li>
            <li>
              <input
                name={InboundFilterKeys.MoveDateStart}
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                onKeyUp={handleInputKeyUp}
                className="form-control filter-input"
                type="date"
                placeholder="Start Date"
                data-testid="filter-input-start-date"
                date-format="yyyy-mm-dd"
                autoFocus
              />
            </li>
            <li>
              <input
                name={InboundFilterKeys.MoveDateEnd}
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                onKeyUp={handleInputKeyUp}
                className="form-control filter-input"
                type="date"
                placeholder="End Date"
                data-testid="filter-input-end-date"
                date-format="yyyy-mm-dd"
              />
            </li>
            <li>
              <a
                id="add-filter"
                onClick={() => setApplyFilter(true)}
                className={classNames({disabled: !startDate.trim() && !endDate.trim()})}
              >
                Add
              </a>
            </li>
            <li>
              <a className="text-danger" onClick={handleCancel}>
                Cancel
              </a>
            </li>
          </>
        );
      }
      case FilterType.Date: {
        return (
          <>
            <li>Filter by {selectedFilter.displayName}:</li>
            <li>
              <input
                name={selectedFilter.key}
                value={filterValue}
                onChange={(e) => setFilterValue(e.target.value)}
                onKeyUp={handleInputKeyUp}
                className="form-control filter-input"
                type="date"
                data-testid="filter-input"
                date-format="yyyy-mm-dd"
              />
            </li>
            <li>
              <a
                id="add-filter"
                onClick={() => setApplyFilter(true)}
                className={classNames({disabled: !filterValue.trim()})}
              >
                Add
              </a>
            </li>
            <li>
              <a className="text-danger" onClick={handleCancel}>
                Cancel
              </a>
            </li>
          </>
        );
      }
      default:
        return null;
    }
  };

  const addFilterControl = (() => {
    return (
      <li>
        <ul className="list-inline">{filterInput()}</ul>
      </li>
    );
  })();

  return (
    <ul className="list-inline list-inline-overflow">
      {addFilterControl}
      {activeFilterPills.length > 0 && <li className="vertical-rule">&nbsp;</li>}
      {activeFilterPills}
    </ul>
  );
};

export default InboundFilter;
